import React, {createContext, useContext, useEffect, useRef, useState} from 'react';
import {useSelector} from 'react-redux';
import client, {url} from '../../lib/api/client';

const empty = {infos:[], alerts:[], updatedAt:null, error:null, loaded:false, readError:null};
const Context = createContext({...empty, hasUnread:false, acknowledge:async () => {}});
export const useDeviceStatus = () => useContext(Context);

export default function DeviceStatusProvider({children}) {
    const {club, user, socketKey} = useSelector(state => ({
        club:state.club.defaultClub, user:state.user.user, socketKey:state.socket.socketKey
    }));
    const clubID = club && club.clubID;
    const playerID = user && user.playerID;
    const enabled = !!(clubID && playerID && socketKey && Number(club.runningTrack) === 1);
    const scope = `${clubID}:${playerID}:${socketKey}`;
    const [state, setState] = useState({...empty, scope});
    const scopeRef = useRef(scope);
    scopeRef.current = scope;
    const confirmed = useRef(new Map());

    useEffect(() => {
        let disposed = false;
        let timer;
        confirmed.current = new Map();
        setState({...empty, scope});
        async function poll() {
            try {
                const {data} = await client.get(`${url}/api/runningtrack/device-status/${clubID}`,
                    {params:{socketKey}, timeout:15000});
                if (disposed) return;
                if (!data || data.result !== 1) throw new Error('Invalid status');
                setState(prev => ({...prev, scope, infos:data.infos, updatedAt:data.updatedAt, loaded:true, error:null,
                    alerts:data.alerts.filter(a => (confirmed.current.get(a.alertID) || 0) < a.revision)}));
            } catch (error) {
                if (!disposed) setState(prev => ({...prev, error:'기기 상태 조회에 실패했습니다. 잠시 후 자동으로 다시 확인합니다.'}));
            } finally {
                if (!disposed) timer = setTimeout(poll, 5000);
            }
        }
        if (enabled) poll();
        return () => {disposed = true; clearTimeout(timer);};
    }, [scope, clubID, socketKey, enabled]);

    const current = state.scope === scope ? state : empty;
    const acknowledge = async () => {
        const capturedScope = scope;
        const alerts = current.alerts.map(({alertID, revision}) => ({alertID, revision}));
        if (!alerts.length || !enabled) return;
        try {
            const {data} = await client.post(`${url}/api/runningtrack/device-status/${clubID}/read`, {alerts},
                {params:{socketKey}, timeout:15000});
            if (scopeRef.current !== capturedScope) return;
            if (!data || data.result !== 1) throw new Error('Read failed');
            alerts.forEach(a => confirmed.current.set(a.alertID, Math.max(confirmed.current.get(a.alertID) || 0, a.revision)));
            setState(prev => ({...prev, readError:null,
                alerts:prev.alerts.filter(a => (confirmed.current.get(a.alertID) || 0) < a.revision)}));
        } catch (_) {
            if (scopeRef.current === capturedScope) setState(prev => ({...prev, readError:'알림 확인을 저장하지 못했습니다. 설정 탭을 다시 눌러 주세요.'}));
        }
    };
    return <Context.Provider value={{...current, hasUnread:enabled && current.alerts.length > 0, acknowledge}}>{children}</Context.Provider>;
}
